import { computed, reactive, ref, watch } from '@vue/composition-api'
import { message } from 'ant-design-vue'
import { Table } from 'ant-design-vue/types/table/table'
import { head, isEmpty } from 'lodash'
import i18n from '@/locales/index'

function cloneWithout(origin: Object, list: string[]) {
  const o = Object.create(null)
  Object.keys(origin).forEach(key => {
    if (!list.includes(key)) o[key] = origin[key]
  })
  return o
}

function useTable<T extends { create: () => {}; update: () => {}; read: () => {}; delete: () => {} }>(
  attrs: Partial<Table>,
  curd: T,
  onRowClick?: Function,
  onPaginationChange?: Function,
  onEmpty?: Function
) {
  const currentRecord = ref({})

  const tableAttrs = reactive({
    size: 'small',
    dataSource: [],
    loading: false,
    customRow: record => ({
      on: {
        click() {
          if (typeof tableAttrs.rowKey === 'function') return
          currentRecord.value = record
          if (tableAttrs.rowSelection) tableAttrs.rowSelection.selectedRowKeys = [record[tableAttrs.rowKey]]
        },
      },
    }),
    rowKey: 'id',
    rowSelection: {
      selectedRowKeys: [],
      onChange(keys) {
        tableAttrs.rowSelection.selectedRowKeys = keys
      },
    },
    rowClassName: (record, index) => {
      let color = ''
      let rowKey: any = tableAttrs.rowKey
      if (index % 2 === 1) color = 'secondColor'
      if (typeof tableAttrs.rowKey === 'function') color = ''
      if (record[rowKey] === currentRecord.value[rowKey]) {
        color = 'row-selected'
      }
      return color
    },
    pagination: {
      size: 'big',
      current: 1,
      pageSize: 10,
      total: 0,
      showTotal:total => `${totalFanyi(total)}`,
      showSizeChanger: false,
      onChange(page: number, pageSize: number) {
        if (typeof tableAttrs.pagination === 'boolean') return
        tableAttrs.pagination.current = page
        tableAttrs.pagination.pageSize = pageSize
        if (onPaginationChange) onPaginationChange(page, pageSize)
      },
      onShowSizeChange(current: number, size: number) {
        if (typeof tableAttrs.pagination === 'boolean') return
        tableAttrs.pagination.current = current
        tableAttrs.pagination.pageSize = size
        if (onPaginationChange) onPaginationChange(current, size)
      },
    },
    ...attrs,
    columns: computed(() =>
      attrs.columns
        .map((item, index) =>
          index !== attrs.columns.length - 1
            ? { ...item, width: item.width || 100 / attrs.columns.length + '%', ellipsis: true }
            : { ...item, ellipsis: true }
        )
        .map(item => ({ ...item, title: item.i18n ? i18n.t(item.i18n) : item.title }))
        .map(item => cloneWithout(item, ['i18n']))
    ),
  })

  const isUpdatable = computed(() => tableAttrs.rowSelection.selectedRowKeys.length === 1)
  const isDeletable = computed(() => tableAttrs.rowSelection.selectedRowKeys.length > 0)
  const isPrint = computed(() => tableAttrs.rowSelection.selectedRowKeys.length > 0)

  watch(
    currentRecord,
    val => {
      if (!isEmpty(val) && typeof onRowClick === 'function') onRowClick(val)
      if (isEmpty(val) && typeof onEmpty === 'function') onEmpty()
    },
    { deep: true, immediate: true }
  )

  async function create(...args: Parameters<typeof curd.create>) {
    try {
      await curd.create(...args)
      message.success(i18n.t('page_tpm_check_create_success') || '新建成功')
    } catch (e) {
      throw e
    }
  }

  async function update(...args: Parameters<typeof curd.update>) {
    try {
      await curd.update(...args)
      message.success(i18n.t('page_mdc_setting_edit_success') || '编辑成功')
    } catch (e) {
      throw e
    }
  }

  async function read(...args: Parameters<typeof curd.read>) {
    try {
      tableAttrs.loading = true
      const res: any = await curd.read(...args)
      tableAttrs.dataSource = res.records || res.list || res
      if (typeof tableAttrs.pagination === 'object') tableAttrs.pagination.total = res.total
      currentRecord.value = head(tableAttrs.dataSource) || {}
      if (typeof tableAttrs.rowKey === 'string' && !isEmpty(tableAttrs.rowSelection) && !isEmpty(currentRecord.value))
        tableAttrs.rowSelection.selectedRowKeys = [currentRecord.value[tableAttrs.rowKey]]
      else if (!isEmpty(tableAttrs.rowSelection)) tableAttrs.rowSelection.selectedRowKeys = []
    } catch (e) {
      tableAttrs.dataSource = []
      throw e
    } finally {
      tableAttrs.loading = false
    }
  }

  async function del(...args: Parameters<typeof curd.delete>) {
    try {
      tableAttrs.loading = true
      await curd.delete(...args)
      message.success(i18n.t('page_mdc_setting_delete_success') || '删除成功')
    } catch (e) {
      throw e
    } finally {
      tableAttrs.loading = false
    }
  }

  const totalFanyi = (number) => {
    const total:any = i18n.t("Total…items");
    return total.replace("{0}", number);
  }

  return {
    tableAttrs,
    currentRecord,
    isUpdatable,
    isDeletable,
    isPrint,

    create,
    update,
    read,
    del,
    totalFanyi,
  }
}

export default useTable
